import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import { listInvitations, cancelInvitation, bulkUploadInvitations } from '../../api/invitations';
import { InvitationInfo } from '../../types';

export const InvitationManagement: React.FC = () => {
  const { tokens } = useAuth();
  const [invitations, setInvitations] = useState<InvitationInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<InvitationInfo | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!tokens?.accessToken) {
        setInvitations([]);
        return;
      }
      const response = await listInvitations(tokens.accessToken);
      const safeInvites = Array.isArray((response as any)?.invitations)
        ? (response as any).invitations
        : [];
      setInvitations(safeInvites as any);
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError(err.response?.data?.detail || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const handleCancelInvitation = (invitation: InvitationInfo) => {
    setInvitationToCancel(invitation);
    setCancelDialogOpen(true);
  };

  const confirmCancelInvitation = async () => {
    if (!invitationToCancel || !tokens?.accessToken) return;

    setCancelling(true);
    try {
      await cancelInvitation(invitationToCancel.invitation_id, tokens.accessToken);
      setCancelDialogOpen(false);
      setInvitationToCancel(null);
      await loadInvitations(); // Refresh the list
    } catch (err: any) {
      console.error('Error cancelling invitation:', err);
      setError(err.response?.data?.detail || 'Failed to cancel invitation');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusChip = (invitation: InvitationInfo) => {
    if (invitation.is_used) {
      const deleted = (invitation as any)?.accepted_user_deleted === true;
      return (
        <Chip
          icon={<CheckIcon />}
          label={deleted ? 'Used (deleted)' : 'Used'}
          color={deleted ? 'default' : 'success'}
          size="small"
        />
      );
    }

    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);
    
    if (expiresAt < now) {
      return <Chip icon={<CancelIcon />} label="Expired" color="error" size="small" />;
    }

    return <Chip icon={<TimeIcon />} label="Pending" color="warning" size="small" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredInvitations = invitations.filter(invitation =>
    invitation.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invitation.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invitation.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Manage User Invitations
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInvitations}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search invitations by email, first name, or last name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Bulk Upload Section */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6">Bulk Invite (CSV)</Typography>
          <Typography variant="body2" color="text.secondary">
            CSV headers: email, first_name, last_name, company, role, countries, value_chains, graph_sources, financial, data_permissions, expires_in_hours.
            Use pipe separators for multi-valued fields (e.g., Romania|Germany or Romania:World Bank|Denmark:CVR).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => {
                const template =
`email,first_name,last_name,company,role,countries,value_chains,graph_sources,financial,data_permissions,expires_in_hours
new.user@example.com,New,User,Acme,user,"Romania|Germany","electronics:root:1234","solar_i3t_6|wind_i3t_2","Romania:World Bank","admin|prodcom_view",168
`;
                const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'invites_template.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download Template
            </Button>
            <Button
              component="label"
              variant="contained"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={async (e) => {
                  // Capture the input element BEFORE awaiting (React event pooling)
                  const inputEl = e.currentTarget as HTMLInputElement;
                  const file = inputEl.files?.[0];
                  if (!file || !tokens?.accessToken) return;
                  setError(null);
                  setUploadResult(null);
                  setUploading(true);
                  try {
                    const result = await bulkUploadInvitations(file, dryRun, tokens.accessToken);
                    setUploadResult(result);
                    if (!dryRun) {
                      await loadInvitations();
                    }
                  } catch (err: any) {
                    console.error('Bulk upload failed:', err);
                    setError(err?.message || 'Bulk upload failed');
                  } finally {
                    setUploading(false);
                    // reset input value to allow re-upload of the same file
                    if (inputEl) inputEl.value = '';
                  }
                }}
              />
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={dryRun ? 'Dry Run' : 'Apply'}
                color={dryRun ? 'default' : 'success'}
                onClick={() => setDryRun(!dryRun)}
              />
              <Typography variant="caption" color="text.secondary">
                Click chip to toggle Dry Run / Apply
              </Typography>
            </Box>
          </Box>
          {uploadResult && (
            <Alert severity={uploadResult.errors?.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
              <div>
                <strong>{dryRun ? 'Dry run' : 'Upload'}</strong> processed {uploadResult.processed} rows.
                {!dryRun && <> Created: {uploadResult.created}. Skipped invites: {uploadResult.skipped_existing_invites}. Skipped users: {uploadResult.skipped_existing_users}.</>}
              </div>
              {Array.isArray(uploadResult.errors) && uploadResult.errors.length > 0 && (
                <div>
                  First errors:
                  <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    {uploadResult.errors.slice(0, 5).map((e: any, i: number) => (
                      <li key={i}>row {e.row}{e.email ? ` (${e.email})` : ''}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No invitations match your search.' : 'No invitations found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvitations.map((invitation) => (
                    <TableRow
                      key={invitation.invitation_id}
                      sx={{ opacity: invitation.is_used && (invitation as any)?.accepted_user_deleted ? 0.55 : 1 }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          {invitation.email}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {invitation.first_name && invitation.last_name
                          ? `${invitation.first_name} ${invitation.last_name}`
                          : invitation.first_name || invitation.last_name || '-'
                        }
                      </TableCell>
                      <TableCell>{invitation.company || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={invitation.role.toUpperCase()} 
                          size="small" 
                          color={invitation.role === 'admin' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(invitation)}</TableCell>
                      <TableCell>{formatDate(invitation.created_at)}</TableCell>
                      <TableCell>{formatDate(invitation.expires_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="Cancel Invitation">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelInvitation(invitation)}
                            disabled={invitation.is_used}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Cancel Invitation</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to cancel the invitation for{' '}
              <strong>{invitationToCancel?.email}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. The user will no longer be able to accept this invitation.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              Cancel
            </Button>
            <Button
              onClick={confirmCancelInvitation}
              color="error"
              variant="contained"
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={20} /> : null}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Invitation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};